import { fetchAuthToken } from "./getCognitoToken";

const MAX_RETRIES = 2; // Allow 2 retries before giving up

export const addTokenRefreshInterceptor = (axiosInstance) => {
  // Per-instance state to avoid shared state across multiple axios instances
  let isRefreshing = false;
  let failedQueue = [];
  let retryCount = 0;

  // Helper function to process all queued requests
  // This captures the local failedQueue variable
  const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
      if (error) reject(error);
      else resolve(token);
    });
    failedQueue = [];
  };

  axiosInstance.interceptors.response.use(
    (response) => {
      // Reset retry count on successful response
      retryCount = 0;
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Skip public endpoints
        const publicEndpoints = [
          "auth/generate-otp",
          "auth/corporateUserLogin/",
          "auth/corporateUserAuthLogin/",
          "auth/mis-user-login",
          "/static/tc/privacy-policy/view",
          "/static/tc/latest/view",
          "/static/tc/eway-bill-consent/view",
          "/static/tc/latest/download",
        ];

        const isPublicEndpoint = publicEndpoints.some((endpoint) =>
          originalRequest.url?.includes(endpoint)
        );

        if (isPublicEndpoint) {
          console.log(
            "401 on public endpoint, not refreshing token:",
            originalRequest.url
          );
          return Promise.reject(error);
        }

        // Check retry count
        if (retryCount >= MAX_RETRIES) {
          console.log(`Max retries (${MAX_RETRIES}) reached, logging out user`);

          // Clear all auth data
          localStorage.removeItem("access_token");
          localStorage.removeItem("token");
          localStorage.removeItem("isLogin");
          localStorage.removeItem("user");
          localStorage.removeItem("misUserEmail");
          localStorage.removeItem("misUserIdpId");
          localStorage.removeItem("loginTime");
          localStorage.removeItem("portalRef");

          // Reset retry count
          retryCount = 0;
          isRefreshing = false;
          failedQueue = [];

          // Redirect to homepage
          window.location.href = "/";

          return Promise.reject(
            new Error("Authentication failed after multiple attempts")
          );
        }

        if (isRefreshing) {
          console.log("Already refreshing, queueing request");
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              // Update the queued request with the new token
              originalRequest.headers["Authorization"] = token;
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;
        retryCount++; // Increment retry count

        try {
          console.log(
            `Token expired, refreshing (attempt ${retryCount}/${MAX_RETRIES}) for URL:`,
            originalRequest.url
          );
          const newToken = await fetchAuthToken();

          if (newToken) {
            console.log("Token refreshed successfully");
            localStorage.setItem("access_token", newToken);

            // Update the failed request with new token
            originalRequest.headers["Authorization"] = newToken;

            // Process all queued requests with the new token
            processQueue(null, newToken);

            // Reset retry count on success
            retryCount = 0;

            return axiosInstance(originalRequest);
          } else {
            throw new Error("Token refresh failed - no token returned");
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
